import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { H } from "@ui/components/content/heading/A11yHeading";
import { getWalkInDateRange } from "@lib/utils/store-locator-utility/getWalkInDateRange";
import { Text } from "@ui/components/content/text/Text";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { HStack } from "@ui/components/layout/stack/Stack";
import { useContext, useEffect, useState } from "react";
import type { WalkInHours } from "./controller";
import { Controller, dayOfWeek } from "./controller";
import { humanizeDistance } from "./humanize-distance";
import { storeLocator } from "@lib/constants/contentful/storeLocator";
import { storeBadgeStyles } from "./StoreLocator.style";
import { activeSiteConfig } from "@config/site/site-config";
import { hoursNotAvailable } from "./utils/hoursNotAvailable";
import { STORE_ADDRESS } from "@e2e/helpers/store-locator/constants/common";
import { useLocale } from "next-intl";

type AddressProps = {
    name: string;
    street: string;
    postalCode: string;
    city: string;
    walkInHours: WalkInHours;
    longitude: number;
    latitude: number;
    localDate: Date;
    isExpanded?: boolean;
    isModal?: boolean;
    isEccoStore?: boolean;
    storeId?: string;
    hideParcelShopWorkingHours: boolean;
    showParcelLockerInfo: boolean;
};

export const Address = ({
    name,
    street,
    isEccoStore,
    postalCode,
    city,
    walkInHours,
    latitude,
    longitude,
    localDate,
    isExpanded,
    isModal,
    storeId,
    hideParcelShopWorkingHours = false,
    showParcelLockerInfo = false,
}: AddressProps) => {
    const locale = useLocale();
    const { clientLocation, dayOfWeekLabels, showStoreBadge } = useContext(Controller);
    const { getMultiple } = useMicrocopy();
    const [distance, setDistance] = useState<string>("");

    const microcopies = getMultiple(CommonCMS.page, [
        ClickAndCollect.storeLocatorOpen,
        ClickAndCollect.storeLocatorOpens,
        ClickAndCollect.storeLocatorClose,
        ClickAndCollect.storeLocatorClosed,
        storeLocator.eccoStore,
        storeLocator.partnerStore,
        storeLocator.hoursNotAvailable,
        storeLocator.parcelLocker,
    ]);

    const dayIndex = localDate.getDay();
    const walkInHoursOfDay = walkInHours?.[dayOfWeek?.[dayIndex]];
    const closingHour = walkInHoursOfDay?.close?.trim() !== "" ? walkInHoursOfDay?.close : "00:00";
    const getNextDay = dayIndex === 6 ? 0 : dayIndex + 1;
    // We will need to double check if some store have closing hours with minutes, because now we are relying on the hours only
    const isOpeningNextDay = localDate.getHours() >= parseInt(closingHour?.split(":")[0]);
    const openingHourNextDay = `${walkInHours?.[dayOfWeek?.[getNextDay]]?.open} ${
        isOpeningNextDay ? dayOfWeekLabels?.[dayOfWeek?.[getNextDay]]?.slice(0, 3) : ""
    }`;

    const {
        geo: { imperialMetricSystem },
    } = activeSiteConfig;

    const [openDate, closeDate] = getWalkInDateRange(walkInHoursOfDay, localDate);

    const isOpen =
        localDate.getTime() > openDate.getTime() && localDate.getTime() < closeDate.getTime();

    useEffect(() => {
        if (clientLocation) {
            const dist = humanizeDistance(
                {
                    latitude: clientLocation.lat,
                    longitude: clientLocation.lng,
                },
                {
                    latitude,
                    longitude,
                },
                locale,
                !imperialMetricSystem ? "metric" : "imperial"
            );

            setDistance(dist);
        }
    }, [clientLocation, latitude, locale, longitude]);

    return (
        <Box width="100%" data-testid={STORE_ADDRESS}>
            {showParcelLockerInfo && (
                <H size={"h6"} gap={1}>
                    {microcopies[storeLocator.parcelLocker]} {storeId}
                </H>
            )}
            <H size="h6" mb={isModal ? 4 : 1}>
                {name}
            </H>
            <Text color="gray.800">{street}</Text>
            <Text color="gray.800">
                {postalCode} {city}
            </Text>
            {showStoreBadge && (
                <Box sx={storeBadgeStyles(isEccoStore)}>
                    {isEccoStore
                        ? microcopies[storeLocator.eccoStore]
                        : microcopies[storeLocator.partnerStore]}
                </Box>
            )}
            <Box display={isExpanded && "none"} mt={4}>
                <Flex justifyContent={!hideParcelShopWorkingHours ? "space-between" : "flex-end"}>
                    {!hideParcelShopWorkingHours && (
                        <HStack>
                            {hoursNotAvailable(walkInHours) ? (
                                <Text pr={5} size="b2">
                                    {microcopies[storeLocator.hoursNotAvailable]}
                                </Text>
                            ) : (
                                <Text size="b2">
                                    {isOpen
                                        ? microcopies[ClickAndCollect.storeLocatorOpen]
                                        : microcopies[ClickAndCollect.storeLocatorClosed]}
                                    <Text as="span" mx={1}>
                                        |
                                    </Text>
                                    {isOpen
                                        ? `${microcopies[ClickAndCollect.storeLocatorClose]} ${closingHour}`
                                        : `${
                                              microcopies[ClickAndCollect.storeLocatorOpens]
                                          } ${openingHourNextDay}`}
                                </Text>
                            )}
                        </HStack>
                    )}
                    {distance && (
                        <Text sx={{ textWrap: "nowrap" }} size="b2">
                            {distance}
                        </Text>
                    )}
                </Flex>
            </Box>
        </Box>
    );
};
